// Trusted plan persistence RPC wrappers (Phase 0009 / 0009b).
//
// The browser is a thin renderer of server-authoritative state.
// This module wraps the five persistence RPCs added by migration
// 0009 and never sends customer_id, nutrition values, allergy or
// restriction decisions, or ownership hints.  All customer-safe
// error messages are surfaced through the `error` field; the raw
// Supabase error is logged sanitized under [etff:plan].

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:plan]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

function customerSafe(label) {
  return {
    load_candidates: "We couldn't load replacement options.",
    swap:            "We couldn't update this meal.",
    select:          "We couldn't save that selection.",
    side:            "We couldn't save that change.",
    get_selected:    "We couldn't load your saved plan.",
  }[label] || "Something went wrong. Please try again.";
}

async function callRpc(label, fn, args) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: customerSafe(label) };
  }
  try {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) {
      logSanitized(`${fn}:error`, error);
      return { ok: false, data: null, error: customerSafe(label) };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized(`${fn}:threw`, err);
    return { ok: false, data: null, error: customerSafe(label) };
  }
}

// ------------------------------------------------------------------
// select_meal_plan(p_plan_id)
// ------------------------------------------------------------------
export function rpcSelectMealPlan(planId) {
  return callRpc('select', 'select_meal_plan', { p_plan_id: planId });
}

// ------------------------------------------------------------------
// get_selected_meal_plan()  — no parameters
// ------------------------------------------------------------------
export function rpcGetSelectedMealPlan() {
  return callRpc('get_selected', 'get_selected_meal_plan', {});
}

// ------------------------------------------------------------------
// list_safe_replacement_candidates(...)
// ------------------------------------------------------------------
export function rpcListSafeReplacementCandidates({
  generation_id, plan_id, day_number, slot, component_order,
}) {
  return callRpc('load_candidates', 'list_safe_replacement_candidates', {
    p_generation_id: generation_id,
    p_plan_id: plan_id,
    p_day_number: day_number,
    p_slot: slot,
    p_component_order: component_order,
  });
}

// ------------------------------------------------------------------
// swap_meal_plan_item(...)
// ------------------------------------------------------------------
export function rpcSwapMealPlanItem({
  plan_id, day_number, slot, component_order, new_etm_id,
}) {
  return callRpc('swap', 'swap_meal_plan_item', {
    p_plan_id: plan_id,
    p_day_number: day_number,
    p_slot: slot,
    p_component_order: component_order,
    p_new_etm_id: new_etm_id,
  });
}

// ------------------------------------------------------------------
// set_meal_plan_side_included(...)
// ------------------------------------------------------------------
export function rpcSetMealPlanSideIncluded({
  plan_id, day_number, slot, component_order, included,
}) {
  return callRpc('side', 'set_meal_plan_side_included', {
    p_plan_id: plan_id,
    p_day_number: day_number,
    p_slot: slot,
    p_component_order: component_order,
    p_included: !!included,
  });
}

// ------------------------------------------------------------------
// Tolerant readers for the persisted-selected-plan contract.
// ------------------------------------------------------------------
function pickStr(o, k){ if(!o) return null; for(const x of k){const v=o[x]; if(typeof v==='string' && v.length) return v;} return null; }
function pickNum(o, k){ if(!o) return null; for(const x of k){const v=o[x]; if(v==null||v==='')continue; const n=Number(v); if(Number.isFinite(n)) return n;} return null; }
function pickArr(o, k){ if(!o) return null; for(const x of k){const v=o[x]; if(Array.isArray(v)) return v;} return null; }
function pickObj(o, k){ if(!o) return null; for(const x of k){const v=o[x]; if(v && typeof v==='object' && !Array.isArray(v)) return v;} return null; }
function pickBool(o, k, def){ if(!o) return def; for(const x of k){const v=o[x]; if(v===true||v===false) return v;} return def; }

export function readSelectedPlanOutcome(payload) {
  return pickStr(payload, ['outcome']) || null;
}
export function hasSelectedPlan(payload) {
  const o = readSelectedPlanOutcome(payload);
  return o && o !== 'no_selected_plan';
}

function readItem(raw) {
  return {
    day_number: pickNum(raw, ['day_number']) ?? 1,
    slot: pickNum(raw, ['slot']) ?? 1,
    component_order: pickNum(raw, ['component_order']) ?? 1,
    role: pickStr(raw, ['role', 'planner_role', 'meal_role']) || 'core',
    etm_id: pickStr(raw, ['etm_id']),
    product_name: pickStr(raw, ['product_name', 'name']),
    serving_size: pickStr(raw, ['serving_size', 'serving']),
    quantity: pickNum(raw, ['quantity']) ?? 1,
    calories: pickNum(raw, ['calories']),
    protein: pickNum(raw, ['protein_g', 'protein']),
    carbohydrates: pickNum(raw, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNum(raw, ['fat_g', 'fat']),
    major_allergens: pickStr(raw, ['major_allergens', 'allergen_display']),
    customer_included: pickBool(raw, ['customer_included'], true),
    display_order: pickNum(raw, ['display_order']),
  };
}

function readTotals(o) {
  if (!o) return { calories:null, protein:null, carbohydrates:null, fat:null };
  return {
    calories: pickNum(o, ['calories']),
    protein: pickNum(o, ['protein_g', 'protein']),
    carbohydrates: pickNum(o, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNum(o, ['fat_g', 'fat']),
  };
}
function readGaps(o) {
  if (!o) return null;
  return {
    calories: pickNum(o, ['calories']),
    protein: pickNum(o, ['protein_g', 'protein']),
    carbohydrates: pickNum(o, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNum(o, ['fat_g', 'fat']),
  };
}

function readDay(raw) {
  const dn = pickNum(raw, ['day_number']) ?? 1;
  const items = (pickArr(raw, ['items', 'meal_plan_items']) || []).map(readItem);
  items.sort((a, b) => (a.slot - b.slot) || (a.component_order - b.component_order));
  return {
    day_number: dn,
    items,
    totals: readTotals(pickObj(raw, ['totals', 'daily_totals'])),
    target_gaps: (() => {
      const g = pickObj(raw, ['target_gaps', 'gaps', 'target_gap']);
      return g ? readGaps(g) : null;
    })(),
  };
}

// Extract the normalized "current selected plan" shape from any of:
//   * get_selected_meal_plan payload (single-plan envelope)
//   * swap_meal_plan_item / set_meal_plan_side_included payload
//     (per-affected-day envelope with `day` OR full plan)
export function readSelectedPlan(payload) {
  if (!payload) return null;
  const plan = pickObj(payload, ['plan']) || payload;
  const days = pickArr(plan, ['days']);
  return {
    generation_id: pickStr(plan, ['generation_id']),
    plan_id: pickStr(plan, ['plan_id', 'meal_plan_id', 'id']),
    option_label: pickStr(plan, ['option_label']),
    planner_rules_version: pickNum(plan, ['planner_rules_version']),
    meal_dataset_version: pickNum(plan, ['meal_dataset_version']),
    days: Array.isArray(days) ? days.map(readDay).sort((a,b)=>a.day_number-b.day_number) : [],
    explanation: pickStr(plan, ['explanation', 'why', 'summary']),
  };
}

// Extract only the affected-day contract returned by swap or side RPCs.
// The response may include a full plan or just the affected day; return
// { day_number, items, totals, target_gaps } if one can be identified,
// otherwise null.
export function readAffectedDay(payload) {
  if (!payload) return null;
  const day = pickObj(payload, ['day', 'affected_day', 'updated_day']);
  if (day) return readDay(day);
  const plan = readSelectedPlan(payload);
  if (plan && plan.days.length === 1) return plan.days[0];
  return null;
}

// Read a candidate list returned by list_safe_replacement_candidates.
// The RPC may arrive as either:
//   * a top-level array of rows (Postgres SETOF result), OR
//   * an object wrapping the rows in `candidates` / `items` / `rows` /
//     `data`.
// Both shapes are handled.
export function readCandidates(payload) {
  let arr = null;
  if (Array.isArray(payload)) {
    arr = payload;
  } else {
    arr = pickArr(payload, ['candidates', 'items', 'rows', 'data']) || [];
  }
  const out = arr.map((raw) => ({
    etm_id: pickStr(raw, ['etm_id']),
    product_name: pickStr(raw, ['product_name', 'name']),
    serving_size: pickStr(raw, ['serving_size', 'serving']),
    role: pickStr(raw, ['role', 'planner_role']) || 'core',
    calories: pickNum(raw, ['calories']),
    protein: pickNum(raw, ['protein_g', 'protein']),
    carbohydrates: pickNum(raw, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNum(raw, ['fat_g', 'fat']),
    major_allergens: pickStr(raw, ['major_allergens', 'allergen_display']),
    display_order: pickNum(raw, ['display_order']),
  }));
  out.sort((a, b) => {
    const da = a.display_order ?? Number.POSITIVE_INFINITY;
    const db = b.display_order ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return (a.product_name || '').localeCompare(b.product_name || '');
  });
  return out;
}

// Outcome constants for consumer switch statements.
export const SELECT_OUTCOMES = {
  selected: 'selected',
  already_selected: 'already_selected',
  invalid_input: 'invalid_input',
  not_found: 'not_found',
  unavailable: 'unavailable',
};
export const SWAP_OUTCOMES = {
  swapped: 'swapped',
  stale_candidate: 'stale_candidate',
  not_allowed: 'not_allowed',
  not_found: 'not_found',
  unavailable: 'unavailable',
};

export function isSuccessfulSelect(outcome) {
  return outcome === 'selected' || outcome === 'already_selected';
}
export function isSuccessfulSwap(outcome) {
  return outcome === 'swapped';
}
export function isSuccessfulSideChange(outcome) {
  return outcome === 'updated' || outcome === 'unchanged';
}
